import React, { useMemo, createElement } from 'react';
import { Checkbox } from '@mui/material';
import type { ColumnDef } from '@tanstack/react-table';
import type { ColumnMetadata } from '../types';
import { ActionsCell, DataGridCell } from '../components';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { parseDateSafe, compareParsedDates } from '../utils';
import { IconButton } from '@mui/material';

type UseColumnDefinitionsProps<T extends Record<string, unknown>> = {
  columnMetadata: ColumnMetadata[];
  lookupMaps?: Map<string, Record<string, string>>;
  isMobile: boolean;
  enableRowSelection?: boolean;
  onDelete?: (row: T) => void;
  onRestore?: (row: T) => void;
  markedForDeletion?: Set<string>;
  inlineEditable?: boolean;
  isDetailNav?: boolean;
  onDeleteScreenshot?: (row: T) => void;
  deletingScreenshotIds?: Set<string>;
  onArchiveFinanceEmail?: (row: T) => void;
  onRestoreFinanceEmail?: (row: T) => void;
  onMarkFinanceEmailSpam?: (row: T) => void;
  onUnmarkFinanceEmailSpam?: (row: T) => void;
};

type UseColumnDefinitionsReturn<T> = {
  columns: ColumnDef<T>[];
  defaultColumn: Partial<ColumnDef<T>>;
  columnMetadataMap: Map<string, ColumnMetadata>;
};

/**
 * Resolves the canonical column identifier for a piece of metadata.
 * Backend responses are inconsistent: some return `field`, others only
 * `dataField` (e.g. Umbraco `getgridmetadata` for report-style grids like
 * "MyTasksReport" only ever sends `dataField`, never `field`).
 * This must be the SINGLE source of truth for column id / accessorKey
 * everywhere in this hook, and anything downstream (grouping, summaries,
 * lookupMaps keys) must resolve the id the same way or `row.getValue()`
 * will silently return undefined.
 */
const resolveColumnId = (col: ColumnMetadata, index: number): string =>
  col.field || col.dataField || `column_${index}`;

const isTemporaryRow = <T extends Record<string, unknown>>(row: { original: T }): boolean =>
  String(row.original.Id ?? '').startsWith('temp_');

/**
 * Hook to build TanStack Table column definitions from ColumnMetadata
 */
export const useColumnDefinitions = <T extends Record<string, unknown>>({
  columnMetadata,
  lookupMaps,
  isMobile,
  enableRowSelection = false,
  onDelete,
  onRestore,
  markedForDeletion,
  inlineEditable,
  isDetailNav = false,
  onDeleteScreenshot,
  deletingScreenshotIds,
  onArchiveFinanceEmail,
  onRestoreFinanceEmail,
  onMarkFinanceEmailSpam,
  onUnmarkFinanceEmailSpam,
}: UseColumnDefinitionsProps<T>): UseColumnDefinitionsReturn<T> => {
  // Create a map for quick column metadata lookup, keyed by the SAME
  // resolved id used as the TanStack column id (see resolveColumnId).
  const columnMetadataMap = useMemo(() => {
    const map = new Map<string, ColumnMetadata>();
    columnMetadata.forEach((col, index) => {
      map.set(resolveColumnId(col, index), col);
    });
    return map;
  }, [columnMetadata]);

  // Default column definition with cell renderer
  const defaultColumn: Partial<ColumnDef<T>> = useMemo(
    () => ({
      cell: (context) => createElement(DataGridCell<T>, { context, columnMetadataMap } as any),
    }),
    [columnMetadataMap]
  );

  // Build column definitions
  const columns = useMemo<ColumnDef<T>[]>(() => {
    const allColumns: ColumnDef<T>[] = [];

    if (isDetailNav) {
      allColumns.push({
        id: '_navigate',
        header: '',
        size: 32,
        minSize: 32,
        maxSize: 32,
        enableSorting: false,
        enableResizing: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        cell: (context) => {
          const { row, table } = context;
          return createElement(
            IconButton,
            {
              size: 'small',
              title: 'Open details',
              'aria-label': 'Open details',
              onClick: (e: React.MouseEvent) => {
                e.stopPropagation();
                if (e.detail > 1) return;
                table.setRowSelection({ [row.id]: true });
                table.options.meta?.onRowNavigate?.(row.original);
              },
              onDoubleClick: (e: React.MouseEvent) => {
                e.stopPropagation();
              },
            },
            createElement(ChevronRightIcon, { fontSize: 'small', color: 'action' })
          );
        },
      });
    }

    // Prepend checkbox selection column when any command has isGroup: true
    if (enableRowSelection) {
      allColumns.push({
        id: '_selection',
        size: 36,
        minSize: 36,
        maxSize: 36,
        enableSorting: false,
        enableResizing: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: ({ table }) =>
          createElement(Checkbox, {
            size: 'small',
            checked: table.getIsAllPageRowsSelected(),
            indeterminate: table.getIsSomePageRowsSelected(),
            onChange: table.getToggleAllPageRowsSelectedHandler(),
            sx: { p: 0, width: 20, height: 20 },
            inputProps: { 'aria-label': 'Select all rows' },
          }),
        cell: ({ row }) => {
          const rowId = String((row.original as Record<string, unknown>).Id || '');
          if (rowId.startsWith('temp_')) return null;
          return createElement(Checkbox, {
            size: 'small',
            checked: row.getIsSelected(),
            onChange: row.getToggleSelectedHandler(),
            sx: { p: 0, width: 20, height: 20 },
            inputProps: { 'aria-label': 'Select row' },
          });
        },
      });
    }

    const dataColumns: ColumnDef<T>[] = columnMetadata
      .filter((col) => col.visible !== false)
      .map((col, index) => {
        const isBoolean = col.type === 'boolean' || col.dataType === 'boolean';
        const rawWidth = col.width != null ? String(col.width).trim() : '';
        const isPercentageWidth = rawWidth.endsWith('%');
        const numericWidth = rawWidth && !isPercentageWidth ? parseInt(rawWidth, 10) : undefined;
        const baseWidth = numericWidth || 120;
        const responsiveWidth = isMobile ? Math.min(baseWidth, 100) : baseWidth;

        const columnId = resolveColumnId(col, index);
        const lookupKey = col.dataField || col.field;
        const isLookupColumn = !!(lookupKey && lookupMaps?.has(lookupKey));
        const isDateTimeColumn = col.type === 'datetime' || col.dataType === 'datetime';
        const isDateOnlyColumn = col.type === 'date' || col.dataType === 'date';

        return {
          id: columnId,
          accessorKey: columnId,
          header: col.title || col.caption || columnId,
          ...(isPercentageWidth
            ? {
                // Percentage width: pass as meta so getColumnWidth() returns CSS value.
                // No px size — let the browser distribute space.
                meta: { width: rawWidth },
              }
            : {
                // Pixel width: use TanStack sizing so resize/mobile clamp works correctly.
                size: responsiveWidth,
                minSize: isMobile ? 80 : 90,
                maxSize: isMobile ? 250 : 300,
              }),
          enableSorting: col.allowSorting ?? true,
          enableResizing: true,
          ...(isBoolean && {
            filterFn: (row, columnId, filterValue) => {
              if (isTemporaryRow(row)) return true;
              if (!filterValue) return true;

              const cellValue = row.getValue(columnId);
              const filterIsTrue = filterValue === '1';

              if (filterIsTrue) {
                return (
                  cellValue === true || cellValue === 'true' || cellValue === '1' || cellValue === 1
                );
              }

              return (
                cellValue === false ||
                cellValue === 'false' ||
                cellValue === '0' ||
                cellValue === 0 ||
                cellValue === '' ||
                cellValue === null ||
                cellValue === undefined
              );
            },
          }),
          ...(!isBoolean &&
            !isLookupColumn && {
              filterFn: (row, columnId, filterValue) => {
                if (isTemporaryRow(row)) return true;
                if (!filterValue) return true;
                const { op, val } =
                  filterValue != null && typeof filterValue === 'object'
                    ? (filterValue as { op: string; val: string })
                    : { op: 'contains', val: String(filterValue) };
                if (!val) return true;
                const rawValue = String(row.getValue(columnId) ?? '');
                const r = rawValue.toLowerCase();
                const v = val.toLowerCase();

                // Text-only operators
                if (op === 'startswith') return r.startsWith(v);
                if (op === 'endswith') return r.endsWith(v);
                if (op === 'notcontains') return !r.includes(v);

                // Comparison operators — try date, then number, then string
                const rawDate = parseDateSafe(rawValue);
                const valDate = parseDateSafe(val);

                if (rawDate && valDate) {
                  const dateMode =
                    isDateTimeColumn || (!isDateOnlyColumn && rawValue.includes('T'))
                      ? 'datetime'
                      : 'date';
                  const dateCompare = compareParsedDates(rawDate, valDate, op, dateMode);
                  if (dateCompare !== null) {
                    return dateCompare;
                  }
                }

                const rn = Number(rawValue);
                const vn = Number(val);
                const bothNumbers = !isNaN(rn) && !isNaN(vn);
                if (bothNumbers) {
                  if (op === '=') return rn === vn;
                  if (op === '<>') return rn !== vn;
                  if (op === '<') return rn < vn;
                  if (op === '<=') return rn <= vn;
                  if (op === '>') return rn > vn;
                  if (op === '>=') return rn >= vn;
                }

                // String fallback
                if (op === '=') return r === v;
                if (op === '<>') return r !== v;
                return r.includes(v); // default: contains
              },
            }),
          ...(!isBoolean &&
            isLookupColumn && {
              filterFn: (row, columnId, filterValue) => {
                if (isTemporaryRow(row)) return true;
                if (!filterValue) return true;
                const lookupMap = lookupMaps?.get(lookupKey);
                const rawValue = String(row.getValue(columnId) ?? '');
                const displayValue = lookupMap?.[rawValue] ?? rawValue;
                const filterStr = String(filterValue).toLowerCase();
                if (
                  lookupMap &&
                  Object.prototype.hasOwnProperty.call(lookupMap, String(filterValue))
                ) {
                  return rawValue === String(filterValue);
                }
                return displayValue.toLowerCase().includes(filterStr);
              },
            }),
        };
      });

    allColumns.push(...dataColumns);

    if (
      onDelete ||
      onRestore ||
      onDeleteScreenshot ||
      onArchiveFinanceEmail ||
      onRestoreFinanceEmail ||
      onMarkFinanceEmailSpam ||
      onUnmarkFinanceEmailSpam
    ) {
      const actionSlots = [
        onDeleteScreenshot,
        onArchiveFinanceEmail,
        onMarkFinanceEmailSpam,
        onUnmarkFinanceEmailSpam,
        onDelete,
        onRestore,
        onRestoreFinanceEmail,
      ].filter(Boolean).length;
      const actionsWidth = Math.min(120, Math.max(44, actionSlots * 36));

      allColumns.push({
        id: '_actions',
        header: '',
        size: actionsWidth,
        minSize: 40,
        maxSize: actionsWidth,
        enableSorting: false,
        enableResizing: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        cell: (context) =>
          createElement(ActionsCell<T>, {
            context,
            onDelete,
            onRestore,
            onDeleteScreenshot,
            onArchiveFinanceEmail,
            onRestoreFinanceEmail,
            onMarkFinanceEmailSpam,
            onUnmarkFinanceEmailSpam,
            markedForDeletion,
            inlineEditable,
            deletingScreenshotIds,
          }),
      });
    }

    return allColumns;
  }, [
    columnMetadata,
    lookupMaps,
    isMobile,
    enableRowSelection,
    onDelete,
    onRestore,
    markedForDeletion,
    inlineEditable,
    isDetailNav,
    deletingScreenshotIds,
    onDeleteScreenshot,
    onArchiveFinanceEmail,
    onRestoreFinanceEmail,
    onMarkFinanceEmailSpam,
    onUnmarkFinanceEmailSpam,
  ]);

  return {
    columns,
    defaultColumn,
    columnMetadataMap,
  };
};
